import React, { useState, useCallback } from 'react';
import cn from 'classnames';

import ColorConstants from 'Wpds/colors/ColorConstants';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Spinner from 'Shared/components/Spinner';

import Transcription from './Transcription';
import VideoPlayerModal from './VideoPlayerModal/VideoPlayerModal';
import AudioPlayer from './AudioPlayer/AudioPlayer';

import './VideoPlayer.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  videoID: number;
  previewImageURL: string;
  transcription?: string;
  audioOnly: boolean;
  hideLoading?: boolean;
  sources: Array<string>;
  className?: string;
  withoutTranscription?: boolean;
};

function VideoPlayer({
  videoID,
  previewImageURL,
  sources,
  transcription,
  audioOnly,
  hideLoading = false,
  className = '',
  withoutTranscription = false,
}: Props): React.ReactElement {
  const [showVideo, setShowVideo] = useState(false);
  const { appId } = useApp();
  const { tr } = useTranslation();

  const handleVideoHide = useCallback(() => setShowVideo(false), []);

  const loading = !hideLoading && !videoID;

  const showAndTrackVideo = useCallback(() => {
    (window as any).analytics.track('Viewed Video', { app_id: appId });
    setShowVideo(true);
  }, [appId]);

  if (audioOnly) {
    return (
      <AudioPlayer
        sources={sources}
        transcription={transcription}
        className={className}
        withoutTranscription={withoutTranscription}
      />
    );
  }

  return (
    <>
      <div className={`VideoPlayer d-flex ${className}`}>
        <div
          className={cn('VideoPlayer__preview d-flex', { 'mr-3': !withoutTranscription })}
          onClick={showAndTrackVideo}
        >
          <img className="VideoPlayer__preview__image" src={previewImageURL} alt="" width="200px" />
          {loading ? (
            <div className="VideoPlayer__preview__loading d-flex align-items-center flex-column justify-content-center pt-2">
              <Spinner activeIf />
              <p className="text-center small">{tr('perform.video.player.uploading')}</p>
            </div>
          ) : (
            <span className="VideoPlayer__preview__icon">
              <WpIcon size="md" prefix="fas" name="play" color={ColorConstants.white[0]} />
            </span>
          )}
        </div>
        {withoutTranscription ? null : <Transcription className="transcription flex-grow-1" text={transcription} />}
      </div>

      <VideoPlayerModal isOpen={showVideo} onToggle={handleVideoHide} sources={sources} transcription={transcription} />
    </>
  );
}

export default VideoPlayer;
